import React, { useCallback, useEffect, useState } from 'react';

const evidenceLabel = { hardware_mac: 'Matching hardware address', name_or_alias: 'Matching name or alias',
  network_address: 'Matching network address', same_area: 'Same area' };

export default function EquipmentIdentityLinks({ areaId }) {
  const [data, setData] = useState({ candidates: [], links: [] });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const token = localStorage.getItem('token');
  const load = useCallback(async (signal) => {
    const response = await fetch(`/api/areas/${areaId}/equipment/link-candidates`, {
      headers: { Authorization: `Bearer ${token}` }, signal,
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || 'Could not load registration matches');
    if (!signal?.aborted) setData(result);
  }, [areaId, token]);

  useEffect(() => {
    const controller = new AbortController();
    setData({ candidates: [], links: [] });
    setMessage('');
    load(controller.signal).catch((error) => !controller.signal.aborted && setMessage(error.message));
    return () => controller.abort();
  }, [load]);

  const refresh = async () => {
    setBusy(true);
    setMessage('');
    try { await load(); } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };
  const confirm = async (candidate) => {
    setBusy(true);
    setMessage('');
    try {
      const response = await fetch(`/api/areas/${areaId}/equipment/${candidate.equipmentId}/link-registration`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ registeredDeviceId: candidate.registeredDeviceId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Could not link equipment');
      await load();
      setMessage(`${candidate.equipmentName} linked. Its Screen connections and settings are retained.`);
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  return <section className="topology-setup__screen" aria-label="Equipment registration links">
    <h4>Link equipment to its Suitestream app</h4>
    <p>Confirm only when both entries are the same physical appliance. Linking keeps the equipment's Screen connections.</p>
    <button type="button" disabled={busy} onClick={refresh}>{busy ? 'Checking…' : 'Refresh registration matches'}</button>
    {!data.candidates.length && !data.links.length && <p>No registration matches yet. Check again after installing and registering the app.</p>}
    {data.candidates.map((candidate) => <div key={`${candidate.equipmentId}-${candidate.registeredDeviceId}`}>
      <p><strong>{candidate.equipmentName}</strong> → {candidate.registeredDeviceName}</p>
      <small>{candidate.registeredAreaName || `Area ${candidate.registeredAreaId}`} · Registered device #{candidate.registeredDeviceId}</small>
      <p>{candidate.evidence.map((entry) => evidenceLabel[entry.type] || entry.type).join(' · ')}</p>
      <button className="equipment-confirm-link" type="button" disabled={busy} onClick={() => confirm(candidate)}>Confirm link: {candidate.equipmentName} to {candidate.registeredDeviceName}</button>
    </div>)}
    {data.links.map((link) => <div key={link.equipmentId}>
      <p><strong>{link.equipmentName}</strong> linked to {link.registeredDeviceName}</p>
      {link.history?.length > 0 && <small>{link.history[link.history.length - 1].message}</small>}
    </div>)}
    {message && <p role="status">{message}</p>}
  </section>;
}
